import { useMemo } from "react";
import { Button, Col, Container, Form, Row } from "react-bootstrap";
import { Formik } from "formik";
import * as Yup from "yup";
import swal from "sweetalert";
import { useNavigate } from "react-router-dom";
import { aiService } from "../../service/api.service";
import { InlineLoader } from "../loader";
import Breadcrumbs from "../common/Breadcrumbs.jsx";
import {
  SUBSCRIPTION_PLANS,
  SUBSCRIPTION_TYPES,
  PAYMENT_TYPES,
  DEFAULT_MODULES,
  getTodayDateValue,
  generateContractRef,
} from "./subscriptionPlans.js";

const ACCESS_OPTIONS = ["All", "Chat", "Calls"];

function needsOpenAi(accessOption) {
  return accessOption === "All" || accessOption === "Chat";
}

function needsTwilio(accessOption) {
  return accessOption === "All" || accessOption === "Calls";
}

function getModulesForAccess(accessOption) {
  if (accessOption === "Chat") {
    return DEFAULT_MODULES.filter((module) => module !== "Calls");
  }
  if (accessOption === "Calls") {
    return ["Dashboard", "Agents", "Calls", "Configuration"];
  }
  return Array.from(new Set([...DEFAULT_MODULES, "Calls"]));
}

function generatePassword() {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789@#$";
  return Array.from(
    { length: 10 },
    () => chars[Math.floor(Math.random() * chars.length)],
  ).join("");
}

function CreateUser() {
  const navigate = useNavigate();
  const initialPassword = useMemo(() => generatePassword(), []);
  const initialContractRef = useMemo(() => generateContractRef(), []);

  const validationSchema = Yup.object().shape({
    name: Yup.string().trim().required("Name is required"),
    email: Yup.string()
      .trim()
      .email("Invalid email format")
      .required("Email is required"),
    password: Yup.string()
      .trim()
      .min(6, "Password must be at least 6 characters long")
      .required("Password is required"),
    subscriptionType: Yup.string().required("Subscription type is required"),
    subscriptionStarts: Yup.string().required("Subscription start is required"),
    allowedAgents: Yup.string().trim().required("Number of agents is required"),
    paymentType: Yup.string().required("Payment type is required"),
    subscriptionPrice: Yup.string().trim().required("Price is required"),
    accessOption: Yup.string()
      .oneOf(ACCESS_OPTIONS)
      .required("Access option is required"),
    openAiApi: Yup.string().trim(),
    twilioSid: Yup.string().trim(),
    twilioSecretKey: Yup.string().trim(),
  });

  return (
    <div className="dashboard-main-area company-list-area create-user-page">
      <Container fluid>
        <div className="dashboard-area-heading management-heading">
          <div className="create-user-shell create-company-shell">
            <div className="create-user-heading">
              <Breadcrumbs />
              <h1 className="company-page-title">New company account</h1>
              <p className="company-page-subtitle">
                Create a company account and its first administrator.
              </p>
            </div>

            <Formik
              initialValues={{
                name: "",
                email: "",
                phone: "",
                password: initialPassword,
                subscriptionType: "Trial",
                subscriptionStarts: getTodayDateValue(),
                subscriptionEnds: "",
                accessOption: "All",
                allowedAgents: String(SUBSCRIPTION_PLANS.Trial.allowedAgents),
                paymentType: PAYMENT_TYPES[0],
                subscriptionPrice: SUBSCRIPTION_PLANS.Trial.price,
                contractRef: initialContractRef,
                internalRemarks: "",
                modules: DEFAULT_MODULES,
                openAiApi: "",
                twilioSid: "",
                twilioSecretKey: "",
              }}
              validationSchema={validationSchema}
              onSubmit={async (values, { setSubmitting }) => {
                setSubmitting(true);
                try {
                  const payload = {
                    ...values,
                    subscriptionStarts: getTodayDateValue(),
                    subscriptionStartDate: getTodayDateValue(),
                    subscriptionEndDate: values.subscriptionEnds,
                    trialEndDate:
                      values.subscriptionType === "Trial"
                        ? values.subscriptionEnds
                        : "",
                    renewalDate:
                      values.subscriptionType !== "Trial"
                        ? values.subscriptionEnds
                        : "",
                    openAiApi: needsOpenAi(values.accessOption)
                      ? values.openAiApi
                      : "",
                    twilioSid: needsTwilio(values.accessOption)
                      ? values.twilioSid
                      : "",
                    twilioSecretKey: needsTwilio(values.accessOption)
                      ? values.twilioSecretKey
                      : "",
                  };
                  const signUpRes = await aiService.signUpUser(payload);
                  const newUserId = signUpRes?.data?.data?._id;

                  let subscriptionSetupFailed = false;
                  if (newUserId) {
                    try {
                      await aiService.addUserSubscription(newUserId, {
                        subscriptionType: values.subscriptionType,
                        subscriptionStartDate: payload.subscriptionStartDate,
                        subscriptionEndDate: payload.subscriptionEndDate,
                        subscriptionPrice: values.subscriptionPrice,
                        allowedAgents: values.allowedAgents,
                        paymentType: values.paymentType,
                        accessOption: values.accessOption,
                        contractRef: values.contractRef,
                        internalRemarks: values.internalRemarks,
                        modules: getModulesForAccess(values.accessOption),
                        trialEndDate: payload.trialEndDate,
                        renewalDate: payload.renewalDate,
                      });
                      await aiService.updateUserDetails(newUserId, {
                        modules: getModulesForAccess(values.accessOption),
                        openAiApi: payload.openAiApi,
                        twilioSid: payload.twilioSid,
                        twilioSecretKey: payload.twilioSecretKey,
                      });
                    } catch (subscriptionError) {
                      console.error(
                        "Failed to attach subscription to new company:",
                        subscriptionError,
                      );
                      subscriptionSetupFailed = true;
                    }
                  }

                  swal({
                    icon: subscriptionSetupFailed ? "warning" : "success",
                    text: subscriptionSetupFailed
                      ? "Company account created, but the subscription details could not be saved. Please add them from the company's page."
                      : "Company account created successfully.",
                  }).then(() => {
                    navigate("/company-list");
                  });
                } catch (error) {
                  if (error?.response?.status === 401) {
                    swal({
                      icon: "error",
                      text: "Unauthorized",
                      button: "OK",
                    }).then(() => {
                      localStorage.clear();
                      window.location.href = "/";
                    });
                  } else {
                    swal({
                      icon: "error",
                      text:
                        error?.response?.data?.message ||
                        "Unable to create company account.",
                    });
                  }
                } finally {
                  setSubmitting(false);
                }
              }}
            >
              {({
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
                handleSubmit,
                isSubmitting,
                setFieldValue,
              }) => {
                const showOpenAi = needsOpenAi(values.accessOption);
                const showTwilio = needsTwilio(values.accessOption);

                return (
                  <Form onSubmit={handleSubmit}>
                    <section className="create-user-card">
                      <div className="create-user-card-header">
                        <h2>Company Information</h2>
                      </div>
                      <div className="create-user-card-body">
                        <p className="create-user-card-note">
                          This person becomes the account's primary
                          administrator.
                        </p>
                        <Row>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>
                                Name <span>*</span>
                              </Form.Label>
                              <Form.Control
                                name="name"
                                placeholder="e.g. Priya Anand"
                                value={values.name}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                isInvalid={touched.name && !!errors.name}
                              />
                              <Form.Control.Feedback type="invalid">
                                {errors.name}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>
                                Email <span>*</span>
                              </Form.Label>
                              <Form.Control
                                type="email"
                                name="email"
                                placeholder="priya.anand@example.org"
                                value={values.email}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                isInvalid={touched.email && !!errors.email}
                              />
                              <Form.Control.Feedback type="invalid">
                                {errors.email}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>
                          <Col md={4}>
                            <Form.Group className="mb-3">
                              <Form.Label>Phone</Form.Label>
                              <Form.Control
                                name="phone"
                                placeholder="+1-555-000-0000"
                                value={values.phone}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                            </Form.Group>
                          </Col>
                          <Col md={4}>
                            <Form.Group className="mb-3">
                              <Form.Label>
                                Temporary password <span>*</span>
                              </Form.Label>
                              <div className="create-user-password-row">
                                <Form.Control
                                  type="text"
                                  name="password"
                                  value={values.password}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  isInvalid={
                                    touched.password && !!errors.password
                                  }
                                />
                                <Button
                                  type="button"
                                  variant="light"
                                  className="create-user-icon-btn"
                                  onClick={() =>
                                    setFieldValue(
                                      "password",
                                      generatePassword(),
                                    )
                                  }
                                  title="Generate password"
                                >
                                  <i
                                    className="fa fa-refresh"
                                    aria-hidden="true"
                                  ></i>
                                </Button>
                              </div>
                              <Form.Control.Feedback
                                type="invalid"
                                className={
                                  touched.password && errors.password
                                    ? "d-block"
                                    : ""
                                }
                              >
                                {errors.password}
                              </Form.Control.Feedback>
                              <Form.Text>
                                Shared with the administrator to sign in for the
                                first time.
                              </Form.Text>
                            </Form.Group>
                          </Col>
                        </Row>
                      </div>
                    </section>

                    <section className="create-user-card">
                      <div className="create-user-card-header">
                        <h2>Plan and payments</h2>
                      </div>
                      <div className="create-user-card-body">
                        <Row className="g-3">
                          <Col md={4}>
                            <Form.Group className="mb-3">
                              <Form.Label>
                                Subscription type <span>*</span>
                              </Form.Label>
                              <Form.Select
                                className="filter-select"
                                name="subscriptionType"
                                value={values.subscriptionType}
                                onChange={(event) => {
                                  const subscriptionType = event.target.value;
                                  const plan = SUBSCRIPTION_PLANS[subscriptionType];
                                  setFieldValue("subscriptionType", subscriptionType);
                                  setFieldValue("subscriptionPrice", plan.price);
                                  setFieldValue(
                                    "allowedAgents",
                                    String(plan.allowedAgents),
                                  );
                                }}
                              >
                                {SUBSCRIPTION_TYPES.map((type) => (
                                  <option key={type} value={type}>
                                    {type}
                                  </option>
                                ))}
                              </Form.Select>
                            </Form.Group>
                          </Col>
                          <Col md={4}>
                            <Form.Group className="mb-3">
                              <Form.Label>
                                Number of agents <span>*</span>
                              </Form.Label>
                              <Form.Control
                                name="allowedAgents"
                                placeholder="e.g. 5 or Unlimited"
                                value={values.allowedAgents}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                isInvalid={
                                  touched.allowedAgents && !!errors.allowedAgents
                                }
                              />
                              <Form.Control.Feedback type="invalid">
                                {errors.allowedAgents}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>
                          <Col md={4}>
                            <Form.Group className="mb-3">
                              <Form.Label>
                                Access option <span>*</span>
                              </Form.Label>
                              <Form.Select
                                className="filter-select"
                                name="accessOption"
                                value={values.accessOption}
                                onChange={(event) => {
                                  const accessOption = event.target.value;
                                  setFieldValue("accessOption", accessOption);
                                  if (!needsOpenAi(accessOption))
                                    setFieldValue("openAiApi", "");
                                  if (!needsTwilio(accessOption)) {
                                    setFieldValue("twilioSid", "");
                                    setFieldValue("twilioSecretKey", "");
                                  }
                                }}
                              >
                                {ACCESS_OPTIONS.map((option) => (
                                  <option key={option} value={option}>
                                    {option}
                                  </option>
                                ))}
                              </Form.Select>
                            </Form.Group>
                          </Col>
                          <Col md={4}>
                            <Form.Group className="mb-3">
                              <Form.Label>
                                Payment type <span>*</span>
                              </Form.Label>
                              <Form.Select
                                className="filter-select"
                                name="paymentType"
                                value={values.paymentType}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                isInvalid={
                                  touched.paymentType && !!errors.paymentType
                                }
                              >
                                {PAYMENT_TYPES.map((type) => (
                                  <option key={type} value={type}>
                                    {type}
                                  </option>
                                ))}
                              </Form.Select>
                              <Form.Control.Feedback type="invalid">
                                {errors.paymentType}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>
                          <Col md={4}>
                            <Form.Group className="mb-3">
                              <Form.Label>
                                Price <span>*</span>
                              </Form.Label>
                              <Form.Control
                                name="subscriptionPrice"
                                placeholder="e.g. $49 / month"
                                value={values.subscriptionPrice}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                isInvalid={
                                  touched.subscriptionPrice &&
                                  !!errors.subscriptionPrice
                                }
                              />
                              <Form.Control.Feedback type="invalid">
                                {errors.subscriptionPrice}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>
                          <Col md={4}>
                            <Form.Group className="mb-3">
                              <Form.Label>Payment reference number</Form.Label>
                              <Form.Control
                                name="contractRef"
                                value={values.contractRef}
                                disabled
                                readOnly
                              />
                            </Form.Group>
                          </Col>
                          <Col md={12}>
                            <Form.Group>
                              <Form.Label>Internal remarks</Form.Label>
                              <Form.Control
                                as="textarea"
                                rows={3}
                                name="internalRemarks"
                                placeholder="Notes visible to super admins only..."
                                value={values.internalRemarks}
                                onChange={handleChange}
                              />
                            </Form.Group>
                          </Col>
                        </Row>
                      </div>
                    </section>

                    <section className="create-user-card">
                      <div className="create-user-card-header">
                        <h2>Subscription dates</h2>
                      </div>
                      <div className="create-user-card-body">
                        <Row className="g-3">
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>Subscription Starts</Form.Label>
                              <Form.Control
                                type="date"
                                name="subscriptionStarts"
                                value={values.subscriptionStarts}
                                disabled
                                readOnly
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>Subscription Ends</Form.Label>
                              <Form.Control
                                type="date"
                                name="subscriptionEnds"
                                value={values.subscriptionEnds}
                                onChange={handleChange}
                              />
                            </Form.Group>
                          </Col>
                        </Row>
                      </div>
                    </section>

                    <section className="create-user-card">
                      <div className="create-user-card-header">
                        <h2>API configuration</h2>
                      </div>
                      <div className="create-user-card-body">
                        <Row className="g-3">
                          {showOpenAi ? (
                            <Col md={12}>
                              <Form.Group className="mb-3">
                                <Form.Label>OpenAI API key</Form.Label>
                                <Form.Control
                                  type="password"
                                  name="openAiApi"
                                  placeholder="sk-..."
                                  value={values.openAiApi}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  isInvalid={
                                    touched.openAiApi && !!errors.openAiApi
                                  }
                                />
                                <Form.Control.Feedback type="invalid">
                                  {errors.openAiApi}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Col>
                          ) : null}
                          {showTwilio ? (
                            <>
                              <Col md={6}>
                                <Form.Group className="mb-3">
                                  <Form.Label>Twilio SID</Form.Label>
                                  <Form.Control
                                    name="twilioSid"
                                    placeholder="AC..."
                                    value={values.twilioSid}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    isInvalid={
                                      touched.twilioSid && !!errors.twilioSid
                                    }
                                  />
                                  <Form.Control.Feedback type="invalid">
                                    {errors.twilioSid}
                                  </Form.Control.Feedback>
                                </Form.Group>
                              </Col>
                              <Col md={6}>
                                <Form.Group className="mb-3">
                                  <Form.Label>Twilio secret key</Form.Label>
                                  <Form.Control
                                    type="password"
                                    name="twilioSecretKey"
                                    placeholder="Enter Twilio auth token"
                                    value={values.twilioSecretKey}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    isInvalid={
                                      touched.twilioSecretKey &&
                                      !!errors.twilioSecretKey
                                    }
                                  />
                                  <Form.Control.Feedback type="invalid">
                                    {errors.twilioSecretKey}
                                  </Form.Control.Feedback>
                                </Form.Group>
                              </Col>
                            </>
                          ) : null}
                        </Row>
                      </div>
                    </section>

                    <div className="create-user-actions">
                      <Button
                        type="button"
                        variant="link"
                        className="create-user-cancel-btn"
                        disabled={isSubmitting}
                        onClick={() => navigate("/company-list")}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        variant="primary"
                        className="agent-primary-btn"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          <InlineLoader />
                        ) : (
                          "Create company account"
                        )}
                      </Button>
                    </div>
                  </Form>
                );
              }}
            </Formik>
          </div>
        </div>
      </Container>
    </div>
  );
}

export default CreateUser;
